import { getOfficeSsoToken } from './office';
import { apiRequest, getStoredSession, setStoredSession } from './api';

export type AuthState =
  | { status: 'unauthenticated' }
  | { status: 'authenticating' }
  | { status: 'authenticated'; user: { id: string; email: string; organizationId: string } };

interface SsoExchangeResponse {
  token: string;
  user: { id: string; email: string; organizationId: string };
}

const USER_KEY = 'casemeister.user';

/**
 * Returns the current persisted auth-state on cold start. Session storage
 * scopes per-tab in browsers; in Outlook desktop the taskpane is its own
 * isolated context, so the session lives as long as the pane stays open.
 */
export function getAuthState(): AuthState {
  const token = getStoredSession();
  const userJson = sessionStorage.getItem(USER_KEY);
  if (!token || !userJson) return { status: 'unauthenticated' };
  try {
    const user = JSON.parse(userJson) as SsoExchangeResponse['user'];
    return { status: 'authenticated', user };
  } catch {
    sessionStorage.removeItem(USER_KEY);
    setStoredSession(null);
    return { status: 'unauthenticated' };
  }
}

/**
 * One-tap SSO: get an Office token → exchange for a Casemeister session.
 * Throws on user-cancel or backend rejection (caller surfaces the message).
 */
export async function authenticate(): Promise<AuthState> {
  const officeToken = await getOfficeSsoToken();
  const result = await apiRequest<SsoExchangeResponse>('/api/outlook/auth/sso-exchange', {
    method: 'POST',
    body: { officeToken },
    unauthenticated: true,
  });
  setStoredSession(result.token);
  sessionStorage.setItem(USER_KEY, JSON.stringify(result.user));
  return { status: 'authenticated', user: result.user };
}

export function signOut(): void {
  setStoredSession(null);
  sessionStorage.removeItem(USER_KEY);
}
